import { createRouter, createWebHistory } from "vue-router";
import { authGuard, guestGuard } from "./guards";
import { FEATURES } from "@/config/features";
import { PERMISSIONS } from "@/utils/permissions";
import { PRINTABLE_DOCUMENTS } from "@/config/printableDocuments";
import { reloadForStaleChunk } from "@/utils/staleChunkRecovery";
import type { RouteRecordRaw } from "vue-router";

const TasksListPage = FEATURES.tasksModuleEnabled
  ? () => import("@/pages/tasks/TasksList.vue")
  : () => import("@/pages/tasks/TasksUnavailable.vue");
const TaskDetailsPage = FEATURES.tasksModuleEnabled
  ? () => import("@/pages/tasks/TaskDetails.vue")
  : () => import("@/pages/tasks/TasksUnavailable.vue");

const printRoutes: RouteRecordRaw[] = Object.values(PRINTABLE_DOCUMENTS).map((doc) => ({
  path: `${doc.basePath}/:id/print`,
  name: doc.printRouteName,
  component: () => import("@/pages/documents/DocumentPrint.vue"),
  props: (route) => ({
    docType: doc.type,
    id: String(route.params.id),
    number: typeof route.query.n === "string" ? route.query.n : undefined,
  }),
  meta: {
    requiresAuth: true,
    permission: doc.permission,
    title: "Print",
  },
}));

const routes: RouteRecordRaw[] = [
  // Dashboard
  {
    path: "/",
    name: "Dashboard",
    component: () => import("@/pages/Index.vue"),
    meta: {
      requiresAuth: true,
      title: "Dashboard",
    },
  },

  // Authentication routes
  {
    path: "/auth/login",
    name: "Login",
    component: () => import("@/pages/auth/Login.vue"),
    meta: {
      title: "Login",
    },
    beforeEnter: guestGuard,
  },
  {
    path: "/login",
    redirect: "/auth/login",
  },
  {
    path: "/auth/forgot-password",
    name: "ForgotPassword",
    component: () => import("@/pages/auth/ForgotPassword.vue"),
    meta: {
      title: "Forgot Password",
    },
    beforeEnter: guestGuard,
  },
  {
    path: "/auth/register",
    name: "Register",
    component: () => import("@/pages/auth/Register.vue"),
    meta: {
      title: "Register",
    },
    beforeEnter: guestGuard,
  },
  {
    path: "/register",
    redirect: "/auth/register",
  },
  {
    path: "/auth/reset-password",
    name: "ResetPassword",
    component: () => import("@/pages/auth/ResetPassword.vue"),
    meta: {
      title: "Reset Password",
    },
    beforeEnter: guestGuard,
  },
  {
    path: "/auth/accept-invite",
    name: "AcceptInvite",
    component: () => import("@/pages/auth/AcceptInvitePage.vue"),
    meta: {
      title: "Accept Invitation",
    },
    beforeEnter: guestGuard,
  },
  {
    path: "/accept-invite",
    redirect: (to) => ({ path: "/auth/accept-invite", query: to.query }),
  },
  {
    path: "/auth/verify-email",
    name: "VerifyEmail",
    component: () => import("@/pages/auth/VerifyEmail.vue"),
    meta: {
      title: "Verify Email",
    },
    beforeEnter: guestGuard,
  },
  {
    path: "/login-by-token",
    name: "LoginByToken",
    component: () => import("@/pages/auth/LoginByToken.vue"),
    meta: { title: "Signing in..." },
  },
  {
    path: "/auth/change-password",
    name: "ChangePassword",
    component: () => import("@/pages/auth/ChangePassword.vue"),
    meta: {
      requiresAuth: true,
      title: "Change Password",
    },
  },

  // Public contract signing (token link, no account required)
  {
    path: "/sign/:token",
    name: "SignContract",
    component: () => import("@/pages/public/SignContract.vue"),
    meta: {
      title: "Sign Contract",
    },
    beforeEnter: guestGuard,
  },
  {
    path: "/verify/:token",
    name: "VerifySignature",
    component: () => import("@/pages/public/VerifySignature.vue"),
    meta: {
      title: "Verify Signature",
    },
    beforeEnter: guestGuard,
  },

  // Profile routes
  {
    path: "/profile",
    name: "Profile",
    component: () => import("@/pages/profile/Profile.vue"),
    meta: {
      requiresAuth: true,
      title: "Profile",
    },
  },
  {
    path: "/profile/point-history",
    name: "PointHistory",
    component: () => import("@/pages/profile/PointHistory.vue"),
    meta: {
      requiresAuth: true,
      title: "Point History",
    },
  },
  {
    path: "/profile/withdrawals",
    name: "Withdrawals",
    component: () => import("@/pages/profile/Withdrawals.vue"),
    meta: {
      requiresAuth: true,
      title: "Withdrawals",
    },
  },
  {
    path: "/profile/point-conversions",
    name: "PointConversions",
    component: () => import("@/pages/profile/PointConversions.vue"),
    meta: {
      requiresAuth: true,
      title: "Point Conversions",
    },
  },
  {
    path: "/profile/level",
    name: "UserLevel",
    component: () => import("@/pages/profile/UserLevel.vue"),
    meta: {
      requiresAuth: true,
      title: "User Level",
    },
  },
  {
    path: "/profile/streak",
    name: "UserStreak",
    component: () => import("@/pages/profile/UserLevel.vue"),
    meta: {
      requiresAuth: true,
      title: "User Streak",
    },
  },

  // Settings
  {
    path: "/settings",
    name: "Settings",
    component: () => import("@/pages/settings/Settings.vue"),
    meta: {
      requiresAuth: true,
      title: "Settings",
    },
  },
  {
    path: "/settings/daftra",
    name: "SettingsDaftra",
    component: () => import("@/pages/settings/SettingsDaftra.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.DAFTRA,
      title: "Daftra settings",
    },
  },
  {
    path: "/settings/erpnext",
    name: "SettingsERPNext",
    component: () => import("@/pages/settings/SettingsERPNext.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ERPNEXT_SETTINGS,
      title: "ERPNext settings",
    },
  },
  {
    path: "/settings/document-serials",
    name: "SettingsDocumentSerials",
    component: () => import("@/pages/settings/SettingsDocumentSerials.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.SERIAL_READ,
      title: "Document numbering",
    },
  },
  {
    path: "/settings/payment-methods",
    name: "SettingsPaymentMethods",
    component: () => import("@/pages/settings/SettingsPaymentMethods.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.SETTINGS_READ,
      title: "Payment methods",
    },
  },
  {
    path: "/settings/template-studio",
    name: "TemplateStudio",
    component: () => import("@/pages/settings/TemplateStudioList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.TEMPLATE_READ,
      title: "Template Studio",
    },
  },
  {
    path: "/settings/template-studio/:id",
    name: "TemplateStudioEdit",
    component: () => import("@/pages/settings/TemplateStudioEdit.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.TEMPLATE_READ,
      title: "Edit template",
    },
  },
  {
    path: "/settings/whatsapp",
    alias: "/whatsapp/settings",
    name: "SettingsWhatsApp",
    component: () => import("@/pages/settings/SettingsWhatsApp.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.WHATSAPP_MANAGE,
      title: "WhatsApp settings",
    },
  },

  // WhatsApp module
  {
    path: "/whatsapp/document-requests",
    name: "WhatsAppDocumentRequests",
    component: () => import("@/pages/whatsapp/WhatsAppDocumentRequests.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.WHATSAPP_READ,
      title: "WhatsApp document requests",
    },
  },
  {
    path: "/whatsapp",
    name: "WhatsAppInbox",
    component: () => import("@/pages/whatsapp/WhatsAppInbox.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.WHATSAPP_READ,
      title: "WhatsApp Inbox",
    },
  },
  {
    path: "/whatsapp/:id",
    name: "WhatsAppThread",
    component: () => import("@/pages/whatsapp/WhatsAppThread.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.WHATSAPP_READ,
      title: "WhatsApp conversation",
    },
  },

  // Module routes
  // Clients
  // Create redirect for legacy clients route
  {
    path: "/clients",
    redirect: "/customers",
  },

  // Customers Module
  {
    path: "/customers",
    name: "Customers",
    component: () => import("@/pages/customers/CustomersList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.CLIENTS,
      title: "Customers",
    },
  },
  {
    path: "/share-target",
    name: "ShareTarget",
    component: () => import("@/pages/customers/ShareTarget.vue"),
    meta: {
      requiresAuth: true,
      title: "Share Target",
    },
  },
  {
    path: "/receipt-inbox",
    name: "ReceiptInbox",
    component: () => import("@/pages/payments/ReceiptInbox.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.RECEIPT_INBOX_READ,
      title: "Receipt Inbox",
    },
  },
  {
    path: "/payments",
    name: "PaymentScheduleLink",
    component: () => import("@/pages/payments/PaymentScheduleLink.vue"),
    meta: {
      requiresAuth: true,
      title: "Payment Schedule",
    },
  },
  {
    path: "/customers/add-cr",
    redirect: (to) => {
      const customerId = to.query.customer_id;
      if (typeof customerId === "string" && customerId) {
        return { path: `/customers/${customerId}`, query: { tab: "legal", addCr: "1" } };
      }
      return { path: "/customers" };
    },
  },
  {
    path: "/customers/search-cr",
    redirect: "/customers",
  },
  {
    path: "/customers/cr-licences/:id",
    name: "CRLicenseDetails",
    component: () => import("@/pages/customers/CustomerCRDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.COMMERCIAL_REGISTER,
      title: "CR License Details",
    },
  },
  {
    path: "/customers/:id",
    name: "CustomerDetails",
    component: () => import("@/pages/customers/CustomerDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.CLIENTS,
      title: "Customer Details",
    },
  },

  // CRM Leads
  {
    path: "/leads",
    name: "Leads",
    component: () => import("@/pages/leads/LeadsList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.LEADS,
      title: "Leads",
    },
  },
  {
    path: "/leads/new",
    name: "LeadNew",
    component: () => import("@/pages/leads/LeadsNew.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.LEADS_CREATE,
      title: "New Lead",
    },
  },
  {
    path: "/leads/:id",
    name: "LeadDetails",
    component: () => import("@/pages/leads/LeadDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.LEADS,
      title: "Lead Details",
    },
  },
  {
    path: "/crm/contacts",
    name: "CrmLeadContacts",
    component: () => import("@/pages/crm/CrmLeadContacts.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.LEADS,
      title: "Contacts",
    },
  },
  {
    path: "/crm/contacts/:id",
    name: "CrmContactDetails",
    component: () => import("@/pages/crm/CrmContactDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.LEADS,
      title: "Contact Details",
    },
  },

  // Locations (Addresses)
  {
    path: "/locations",
    name: "Locations",
    component: () => import("@/pages/locations/LocationsList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.CLIENTS,
      title: "Locations",
    },
  },

  // Projects
  {
    path: "/projects",
    name: "Projects",
    component: () => import("@/pages/projects/ProjectsList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.PROJECTS,
      title: "Projects",
    },
  },
  {
    path: "/projects/new",
    name: "AddProject",
    component: () => import("@/pages/projects/AddProject.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.PROJECTS_CREATE,
      title: "Add Project",
    },
  },
  {
    path: "/projects/:id",
    name: "ProjectDetails",
    component: () => import("@/pages/projects/ProjectDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.PROJECTS,
      title: "Project Details",
    },
  },

  // Quotations
  {
    path: "/quotations",
    name: "Quotations",
    component: () => import("@/pages/quotations/QuotationsList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.QUOTATIONS,
      title: "Quotations",
    },
  },
  {
    path: "/quotations/new",
    name: "AddQuotation",
    component: () => import("@/pages/quotations/AddQuotation.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.QUOTATIONS_CREATE,
      title: "Add Quotation",
    },
  },
  {
    path: "/quotations/:id",
    name: "QuotationDetails",
    component: () => import("@/pages/quotations/QuotationDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.QUOTATIONS,
      title: "Quotation Details",
    },
  },

  // Contracts
  {
    path: "/contracts",
    name: "Contracts",
    component: () => import("@/pages/contracts/ContractsList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.CONTRACTS,
      title: "Contracts",
    },
  },
  {
    path: "/contracts/:id",
    name: "ContractDetails",
    component: () => import("@/pages/contracts/ContractDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.CONTRACTS,
      title: "Contract Details",
    },
  },
  {
    path: "/contracts/my-dashboard",
    name: "MyContractsDashboard",
    component: () => import("@/pages/contracts/MyContractsDashboard.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.CONTRACTS,
      title: "My Contracts Dashboard",
    },
  },
  {
    path: "/contracts/new",
    name: "AddContract",
    component: () => import("@/pages/contracts/AddContract.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.CONTRACTS_CREATE,
      title: "Add Contract",
    },
  },

  // Sales Orders
  {
    path: "/sales-orders",
    name: "SalesOrders",
    component: () => import("@/pages/sales-orders/SalesOrdersList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.SALES_ORDERS,
      title: "Sales Orders",
    },
  },
  {
    path: "/sales-orders/new",
    name: "AddSalesOrder",
    component: () => import("@/pages/sales-orders/AddSalesOrder.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.SALES_ORDERS_CREATE,
      title: "Add Sales Order",
    },
  },
  {
    path: "/sales-orders/:id",
    name: "SalesOrderDetails",
    component: () => import("@/pages/sales-orders/SalesOrderDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.SALES_ORDERS,
      title: "Sales Order Details",
    },
  },

  // Invoices
  {
    path: "/invoices",
    name: "Invoices",
    component: () => import("@/pages/invoices/InvoicesList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.INVOICES_VIEW,
      title: "Invoices",
    },
  },
  {
    path: "/invoices/new",
    name: "AddInvoice",
    component: () => import("@/pages/invoices/AddInvoice.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.INVOICES_CREATE,
      title: "Add Invoice",
    },
  },
  {
    path: "/invoices/:id/payments",
    name: "InvoicePayments",
    component: () => import("@/pages/invoices/InvoicePayments.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.PAYMENT_READ,
      title: "Invoice Payments",
    },
  },
  {
    path: "/invoices/:id/edit",
    name: "EditInvoice",
    component: () => import("@/pages/invoices/EditInvoice.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.INVOICES_EDIT,
      title: "Edit Invoice",
    },
  },
  {
    path: "/invoices/:id",
    name: "InvoiceDetails",
    component: () => import("@/pages/invoices/InvoiceDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.INVOICES_VIEW,
      title: "Invoice Details",
    },
  },

  // Credit notes
  {
    path: "/credit-notes",
    name: "CreditNotes",
    component: () => import("@/pages/credit-notes/CreditNotesList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.CREDIT_NOTES_VIEW,
      title: "Credit Notes",
    },
  },
  {
    path: "/credit-notes/new",
    name: "AddCreditNote",
    component: () => import("@/pages/credit-notes/AddCreditNote.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.CREDIT_NOTES_CREATE,
      title: "Add Credit Note",
    },
  },
  {
    path: "/credit-notes/:id",
    name: "CreditNoteDetails",
    component: () => import("@/pages/credit-notes/CreditNoteDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.CREDIT_NOTES_VIEW,
      title: "Credit Note Details",
    },
  },

  // Inventory
  {
    path: "/items",
    name: "ItemsList",
    component: () => import("@/pages/items/ItemsList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ITEMS_VIEW,
      title: "Items",
    },
  },
  {
    path: "/items/new",
    name: "AddItem",
    component: () => import("@/pages/items/AddItem.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ITEMS_CREATE,
      title: "Add Item",
    },
  },
  {
    path: "/items/:id/edit",
    name: "EditItem",
    component: () => import("@/pages/items/EditItem.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ITEMS_EDIT,
      title: "Edit Item",
    },
  },
  {
    path: "/items/:id",
    name: "ItemDetails",
    component: () => import("@/pages/items/ItemDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ITEMS_VIEW,
      title: "Item Details",
    },
  },
  {
    path: "/delivery-notes",
    name: "DeliveryNotesList",
    component: () => import("@/pages/delivery-notes/DeliveryNotesList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.DELIVERY_NOTES_VIEW,
      title: "Delivery Notes",
    },
  },
  {
    path: "/delivery-notes/new",
    name: "AddDeliveryNote",
    component: () => import("@/pages/delivery-notes/AddDeliveryNote.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.DELIVERY_NOTES_CREATE,
      title: "Add Delivery Note",
    },
  },
  {
    path: "/delivery-notes/:id/edit",
    name: "EditDeliveryNote",
    component: () => import("@/pages/delivery-notes/EditDeliveryNote.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.DELIVERY_NOTES_EDIT,
      title: "Edit Delivery Note",
    },
  },
  {
    path: "/delivery-notes/:id",
    name: "DeliveryNoteDetails",
    component: () => import("@/pages/delivery-notes/DeliveryNoteDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.DELIVERY_NOTES_VIEW,
      title: "Delivery Note Details",
    },
  },

  // Accounting
  {
    path: "/accounting",
    name: "Accounting",
    component: () => import("@/pages/accounting/AccountingDashboard.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ACCOUNTING_VIEW,
      title: "Accounting",
    },
  },
  {
    path: "/accounting/chart-of-accounts",
    name: "ChartOfAccounts",
    component: () => import("@/pages/accounting/ChartOfAccounts.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ACCOUNTING_VIEW,
      title: "Chart of Accounts",
    },
  },
  {
    path: "/accounting/journals",
    name: "JournalEntries",
    component: () => import("@/pages/accounting/JournalEntries.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.JOURNAL_LIST,
      title: "Journal Entries",
    },
  },
  {
    path: "/accounting/reports",
    name: "AccountingReports",
    component: () => import("@/pages/accounting/AccountingReports.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ACCOUNTING_REPORTS,
      title: "Accounting Reports",
    },
  },
  {
    path: "/accounting/suppliers",
    name: "AccountingSuppliers",
    component: () => import("@/pages/accounting/SuppliersList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.SUPPLIER_LIST,
      title: "Suppliers",
    },
  },
  {
    path: "/accounting/suppliers/:id",
    name: "AccountingSupplierDetail",
    component: () => import("@/pages/accounting/SupplierDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.SUPPLIER_LIST,
      title: "Supplier details",
    },
  },
  {
    path: "/accounting/bills",
    name: "AccountingBills",
    component: () => import("@/pages/accounting/BillsList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.BILL_LIST,
      title: "Bills",
    },
  },
  {
    path: "/accounting/bills/new",
    name: "AccountingBillNew",
    component: () => import("@/pages/accounting/BillForm.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.BILL_CREATE,
      title: "New bill",
    },
  },
  {
    path: "/accounting/bills/:id/edit",
    name: "AccountingBillEdit",
    component: () => import("@/pages/accounting/BillForm.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.BILL_CREATE,
      title: "Edit bill",
    },
  },
  {
    path: "/accounting/bills/:id",
    name: "AccountingBillDetail",
    component: () => import("@/pages/accounting/BillDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.BILL_LIST,
      title: "Bill details",
    },
  },
  {
    path: "/accounting/payments",
    name: "AccountingPayments",
    component: () => import("@/pages/accounting/PaymentEntriesList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.PAYMENT_ENTRY_LIST,
      title: "Payment Entries",
    },
  },
  {
    path: "/accounting/tax-codes",
    name: "TaxCodes",
    component: () => import("@/pages/accounting/TaxCodesList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.TAX_CODE_LIST,
      title: "Tax Codes",
    },
  },
  {
    path: "/accounting/payment-terms",
    name: "PaymentTerms",
    component: () => import("@/pages/accounting/PaymentTermsList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.QUOTATIONS,
      title: "Payment Terms",
    },
  },
  {
    path: "/accounting/treasuries",
    name: "Treasuries",
    component: () => import("@/pages/accounting/TreasuryList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.TREASURY_LIST,
      title: "Treasuries",
    },
  },
  {
    path: "/accounting/fiscal",
    name: "FiscalPeriods",
    component: () => import("@/pages/accounting/FiscalPeriods.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.FISCAL_YEAR_READ,
      title: "Fiscal Periods",
    },
  },
  {
    path: "/accounting/banking",
    name: "AccountingBanking",
    component: () => import("@/pages/accounting/BankingWorkspace.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.BANK_READ,
      title: "Banking",
    },
  },
  {
    path: "/accounting/settings",
    name: "AccountingSettings",
    component: () => import("@/pages/accounting/AccountingSettings.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ACCOUNTING_SETTINGS_READ,
    },
  },
  {
    path: "/accounting/withholding",
    name: "TaxWithholding",
    component: () => import("@/pages/accounting/TaxWithholding.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.TAX_CODE_LIST,
      title: "Tax Withholding",
    },
  },
  {
    path: "/accounting/fx",
    name: "FxRevaluation",
    component: () => import("@/pages/accounting/FxRevaluation.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.FISCAL_YEAR_READ,
      title: "Currency Revaluation",
    },
  },
  {
    path: "/accounting/integrity",
    name: "LedgerIntegrity",
    component: () => import("@/pages/accounting/LedgerIntegrity.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ACCOUNTING_REPORTS,
      title: "Ledger Integrity",
    },
  },
  {
    path: "/accounting/dunning",
    name: "Dunning",
    component: () => import("@/pages/accounting/Dunning.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ACCOUNTING_REPORTS,
      title: "Dunning",
    },
  },
  {
    path: "/accounting/opening-invoices",
    name: "OpeningInvoices",
    component: () => import("@/pages/accounting/OpeningInvoices.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ACCOUNTING_REPORTS,
      title: "Opening Invoices",
    },
  },
  {
    path: "/accounting/cost-centers",
    name: "CostCenters",
    component: () => import("@/pages/accounting/CostCentersList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ACCOUNTING_VIEW,
      title: "Cost Centers",
    },
  },
  {
    path: "/accounting/currencies",
    name: "CurrenciesAndRates",
    component: () => import("@/pages/accounting/CurrenciesAndRates.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ACCOUNTING_VIEW,
      title: "Currencies & Rates",
    },
  },
  {
    path: "/accounting/erpnext",
    name: "AccountingERPNext",
    component: () => import("@/pages/erpnext/ERPNextSync.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ERPNEXT_SETTINGS,
      title: "ERPNext Sync",
    },
  },

  // Legacy studies routes → projects module
  { path: "/studies", redirect: "/projects" },
  { path: "/studies/new", redirect: "/projects/new" },
  { path: "/studies/supervised", redirect: "/projects" },
  { path: "/studies/trace", redirect: "/projects" },
  { path: "/studies/:id", redirect: (to) => `/projects/${String(to.params.id)}` },
  // Reports
  {
    path: "/reports",
    name: "Reports",
    component: () => import("@/pages/reports/ReportsList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.REPORTS,
      title: "Reports",
    },
  },
  {
    path: "/reports/financial",
    name: "FinancialReport",
    component: () => import("@/pages/reports/FinancialReport.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.REPORTS,
      title: "Financial Report",
    },
  },
  // Tasks & Notifications
  {
    path: "/tasks",
    name: "TasksList",
    component: TasksListPage,
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.TASKS_LIST,
      title: "Tasks",
    },
  },
  {
    path: "/tasks/:id",
    name: "TaskDetails",
    component: TaskDetailsPage,
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.TASKS_READ,
      title: "Task Details",
    },
  },
  {
    path: "/notifications",
    name: "NotificationsList",
    component: () => import("@/pages/notifications/NotificationsList.vue"),
    meta: {
      requiresAuth: true,
      title: "Notifications",
    },
  },

  // Support tickets
  {
    path: "/support/tickets",
    name: "SupportTicketsList",
    component: () => import("@/pages/support/SupportTicketsList.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.SUPPORT_READ,
      title: "Support Tickets",
    },
  },
  {
    path: "/support/tickets/new",
    name: "NewSupportTicket",
    component: () => import("@/pages/support/NewSupportTicket.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.SUPPORT_CREATE,
      title: "New Support Ticket",
    },
  },
  {
    path: "/support/tickets/:id",
    name: "SupportTicketDetails",
    component: () => import("@/pages/support/SupportTicketDetails.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.SUPPORT_READ,
      title: "Support Ticket",
    },
  },

  // Admin
  {
    path: "/admin",
    name: "Admin",
    component: () => import("@/pages/admin/AdminDashboard.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ADMIN,
      title: "Admin",
    },
  },
  {
    path: "/admin/branding",
    name: "AdminBranding",
    component: () => import("@/pages/admin/AdminBranding.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.SETTINGS_READ,
      title: "Company details",
    },
  },
  {
    path: "/admin/jobs",
    name: "AdminJobs",
    component: () => import("@/pages/admin/AdminJobs.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.JOBS_READ,
      title: "Background jobs",
    },
  },
  {
    path: "/admin/project-types",
    name: "ProjectTypesManagement",
    component: () => import("@/pages/admin/ProjectTypesManagement.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.PROJECT_TYPES_MANAGE,
      title: "Project Types",
    },
  },
  {
    path: "/admin/services-configurables",
    name: "ServiceConfigurables",
    component: () => import("@/pages/admin/ServiceConfigurables.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ITEMS_EDIT,
      title: "Service configurables",
    },
  },
  {
    path: "/admin/access",
    component: () => import("@/layouts/AdminAccessLayout.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.ADMIN_USERS,
      title: "Users & Access",
    },
    children: [
      {
        path: "",
        redirect: "/admin/access/users",
      },
      {
        path: "users",
        name: "AccessUsers",
        component: () => import("@/pages/admin/UsersManagement.vue"),
        meta: { title: "User Management", permission: PERMISSIONS.ADMIN_USERS },
      },
      {
        path: "roles",
        name: "AccessRoles",
        component: () => import("@/pages/admin/rbac/RolesManagement.vue"),
        meta: { title: "Roles Management", permission: PERMISSIONS.ROLES_VIEW },
      },
      {
        path: "matrix",
        name: "AccessMatrix",
        component: () => import("@/pages/admin/rbac/RolePermissionsMatrix.vue"),
        meta: { title: "Role-Permissions Matrix", permission: PERMISSIONS.ROLES_EDIT },
      },
    ],
  },
  {
    path: "/admin/users",
    redirect: "/admin/access/users",
  },
  {
    path: "/admin/roles",
    redirect: "/admin/access/roles",
  },
  {
    path: "/admin/permissions",
    redirect: "/admin/access/matrix",
  },
  {
    path: "/admin/role-matrix",
    redirect: "/admin/access/matrix",
  },
  {
    path: "/admin/user-roles",
    redirect: "/admin/access/users",
  },
  {
    path: "/admin/org-structure",
    name: "OrgStructure",
    component: () => import("@/pages/admin/OrgStructure.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.SETTINGS_READ,
      title: "Departments & Sections",
    },
  },
  {
    path: "/admin/sections",
    redirect: "/admin/org-structure",
  },
  {
    path: "/admin/file-types",
    name: "FileTypesManagement",
    component: () => import("@/pages/admin/FileTypesManagement.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.FILE_TYPES_MANAGE,
      title: "Document Categories",
    },
  },
  {
    path: "/admin/branches",
    name: "BranchesManagement",
    component: () => import("@/pages/admin/BranchesManagement.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.BRANCH_READ,
      title: "Branches",
    },
  },

  // Daftra
  {
    path: "/daftra",
    name: "Daftra",
    component: () => import("@/pages/daftra/DaftraSync.vue"),
    meta: {
      requiresAuth: true,
      permission: PERMISSIONS.DAFTRA,
      title: "Daftra Integration",
    },
  },

  // Print view (one route per printable selling document)
  ...printRoutes,

  // Access denied (RBAC route guard)
  {
    path: "/forbidden",
    name: "Forbidden",
    component: () => import("@/pages/errors/ForbiddenPage.vue"),
    meta: {
      requiresAuth: true,
      title: "Access forbidden",
    },
  },

  // Catch all route - 404
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/pages/NotFound.vue"),
    meta: {
      title: "Page Not Found",
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Global navigation guards
router.beforeEach(authGuard);

router.onError((error) => {
  reloadForStaleChunk(error, "router");
});

import { applyDocumentTitle } from '@/utils/documentTitle'

// Update page title
router.afterEach((to) => {
  applyDocumentTitle(to)
});

export default router;
